import { createDtTheme } from './factory';

// ─── High Contrast Light ──────────────────────────────────────────────────────
// Sharper borders, deeper primary, stronger contrast — WCAG AA+ accessible.
export const dtHighContrastLightTheme = createDtTheme({
  variant: 'light',
  background: { default: '#f5f5f5', paper: '#ffffff' },
  text: { primary: '#000000', secondary: '#333333' },
  primary: { main: '#003040', light: '#00566a', dark: '#001a26', contrast: '#ffffff' },
  secondary: { main: '#00566a', light: '#00677f', dark: '#003040', contrast: '#ffffff' },
  border: '#767676',
  accent: '#ffff40',
  action: {
    active: '#003040',
    hover: '#d2e5eb',
    selected: '#a6cad8',
    disabled: '#767676',
    disabledBackground: '#e0e0e0',
  },
  nav: {
    background: '#001a26',
    text: '#ffffff',
    selectedText: '#ffff40',
    itemHover: '#003040',
    submenuBg: '#003040',
  },
  header: {
    background: '#001a26',
    gradient: 'linear-gradient(135deg, #001a26 0%, #003040 50%, #00566a 100%)',
    shadow: '0 2px 8px rgba(0, 26, 38, 0.3)',
    linkHoverColor: '#ffff40',
  },
});

// ─── Warm Sand ────────────────────────────────────────────────────────────────
// Off-white background with warm mocha tones — easy on the eyes for long sessions.
export const dtWarmSandTheme = createDtTheme({
  variant: 'light',
  background: { default: '#faf8f5', paper: '#f5f0ea' },
  text: { primary: '#2a1a10', secondary: '#7a5a44' },
  primary: { main: '#7c5c4a', light: '#c8956b', dark: '#3d2b1f', contrast: '#ffffff' },
  secondary: { main: '#c8956b', light: '#e8c09a', dark: '#7c5c4a', contrast: '#2a1a10' },
  border: '#d0c0b0',
  accent: '#c8956b',
  action: {
    active: '#7c5c4a',
    hover: '#f0e8e0',
    selected: '#e8d8c8',
    disabled: '#b0a090',
    disabledBackground: '#e8e0d8',
  },
  nav: {
    background: '#3d2b1f',
    text: '#f0e8e0',
    selectedText: '#e8c09a',
    itemHover: '#5a3d2b',
    submenuBg: '#5a3d2b',
  },
  header: {
    background: '#3d2b1f',
    gradient: 'linear-gradient(135deg, #3d2b1f 0%, #7c5c4a 50%, #c8956b 100%)',
    shadow: '0 2px 8px rgba(61, 43, 31, 0.2)',
    linkHoverColor: '#e8c09a',
  },
});

// ─── Ocean Blue ───────────────────────────────────────────────────────────────
// Classic deep navy-to-sky blue — professional and trustworthy.
export const dtOceanBlueTheme = createDtTheme({
  variant: 'light',
  background: { default: '#f0f4f8', paper: '#ffffff' },
  text: { primary: '#1a2744', secondary: '#4a6a8a' },
  primary: { main: '#2d5a8e', light: '#5b8ac2', dark: '#1e3a5f', contrast: '#ffffff' },
  secondary: { main: '#5b8ac2', light: '#a8c8e8', dark: '#2d5a8e', contrast: '#ffffff' },
  border: '#c8d8e8',
  accent: '#a8d8f0',
  action: {
    active: '#2d5a8e',
    hover: '#e8f0f8',
    selected: '#c8d8e8',
    disabled: '#8aaccc',
    disabledBackground: '#d8e8f0',
  },
  nav: {
    background: '#1e3a5f',
    text: '#ffffff',
    selectedText: '#a8d8f0',
    itemHover: '#2d5a8e',
    submenuBg: '#2d5a8e',
  },
  header: {
    background: '#1e3a5f',
    gradient: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a8e 50%, #5b8ac2 100%)',
    shadow: '0 2px 8px rgba(30, 58, 95, 0.2)',
    linkHoverColor: '#a8d8f0',
  },
});

// ─── Slate Dimmed ─────────────────────────────────────────────────────────────
// GitHub Dark Dimmed-inspired warm grey-slate — softer on the eyes than pure navy.
export const dtSlateDimmedTheme = createDtTheme({
  variant: 'dark',
  background: { default: '#22272e', paper: '#2d333b' },
  text: { primary: '#adbac7', secondary: '#768390' },
  primary: { main: '#539bf5', light: '#80b4ff', dark: '#316dca', contrast: '#1c2128' },
  secondary: { main: '#539bf5', light: '#80b4ff', dark: '#316dca', contrast: '#1c2128' },
  border: '#444c56',
  divider: '#444c56',
  accent: '#539bf5',
  action: {
    active: '#539bf5',
    hover: 'rgba(83, 155, 245, 0.08)',
    selected: 'rgba(83, 155, 245, 0.16)',
    disabled: '#768390',
    disabledBackground: '#2d333b',
  },
  nav: {
    background: '#1c2128',
    text: '#adbac7',
    selectedText: '#adbac7',
    itemHover: '#2d333b',
    submenuBg: '#2d333b',
  },
  header: {
    background: '#1c2128',
    gradient: 'linear-gradient(135deg, #1c2128 0%, #22272e 100%)',
    shadow: 'none',
    borderBottom: '1px solid #444c56',
    linkHoverColor: '#80b4ff',
  },
});

// ─── Hacker Terminal ──────────────────────────────────────────────────────────
// Phosphor green on near-black — for those who live in the terminal.
export const dtHackerTerminalTheme = createDtTheme({
  variant: 'dark',
  background: { default: '#0d0d0d', paper: '#0a1a0a' },
  text: { primary: '#00ff41', secondary: '#33cc33' },
  primary: { main: '#00ff41', light: '#66ff88', dark: '#00aa2b', contrast: '#000000' },
  secondary: { main: '#33cc33', light: '#66ff66', dark: '#009a20', contrast: '#000000' },
  border: '#1a3a1a',
  divider: '#1a3a1a',
  accent: '#00ff41',
  action: {
    active: '#00ff41',
    hover: 'rgba(0, 255, 65, 0.08)',
    selected: 'rgba(0, 255, 65, 0.16)',
    disabled: '#1a3a1a',
    disabledBackground: '#0a1a0a',
  },
  nav: {
    background: '#050505',
    text: '#00ff41',
    selectedText: '#00ff41',
    itemHover: '#0a1a0a',
    submenuBg: '#0a1a0a',
  },
  header: {
    background: '#050505',
    gradient: 'linear-gradient(135deg, #050505 0%, #0a1a0a 100%)',
    shadow: 'none',
    borderBottom: '1px solid #1a3a1a',
    textColor: '#00ff41',
    linkHoverColor: '#66ff88',
  },
});

// ─── Dusk Purple ─────────────────────────────────────────────────────────────
// Deep violet dark theme — vibrant and expressive.
export const dtDuskPurpleTheme = createDtTheme({
  variant: 'dark',
  background: { default: '#1a0a2e', paper: '#2d1052' },
  text: { primary: '#f0abfc', secondary: '#c084fc' },
  primary: { main: '#c084fc', light: '#e879f9', dark: '#9333ea', contrast: '#1a0a2e' },
  secondary: { main: '#e879f9', light: '#f0abfc', dark: '#c084fc', contrast: '#1a0a2e' },
  border: '#3d1f6b',
  divider: '#3d1f6b',
  accent: '#e879f9',
  action: {
    active: '#c084fc',
    hover: 'rgba(192, 132, 252, 0.08)',
    selected: 'rgba(192, 132, 252, 0.16)',
    disabled: '#7c4aa0',
    disabledBackground: '#2d1052',
  },
  nav: {
    background: '#12061e',
    text: '#f0abfc',
    selectedText: '#e879f9',
    itemHover: '#2d1052',
    submenuBg: '#2d1052',
  },
  header: {
    background: '#12061e',
    gradient: 'linear-gradient(135deg, #12061e 0%, #1a0a2e 50%, #2d1052 100%)',
    shadow: 'none',
    borderBottom: '1px solid #3d1f6b',
    linkHoverColor: '#f0abfc',
  },
});

// ─── Sunrise ─────────────────────────────────────────────────────────────────
// Warm orange-to-amber on cream — energetic and distinctive.
export const dtSunriseTheme = createDtTheme({
  variant: 'light',
  background: { default: '#fff8f0', paper: '#ffffff' },
  text: { primary: '#2a1000', secondary: '#7a3a18' },
  primary: { main: '#e8420a', light: '#ff8c00', dark: '#c84400', contrast: '#ffffff' },
  secondary: { main: '#ff8c00', light: '#ffcc44', dark: '#e8420a', contrast: '#2a1000' },
  border: '#ffd0a0',
  accent: '#ffcc44',
  action: {
    active: '#e8420a',
    hover: '#fff0e0',
    selected: '#ffe0c0',
    disabled: '#c8a080',
    disabledBackground: '#f0e0d0',
  },
  nav: {
    background: '#c84400',
    text: '#ffffff',
    selectedText: '#ffcc44',
    itemHover: '#e8420a',
    submenuBg: '#e8420a',
  },
  header: {
    background: '#c84400',
    gradient: 'linear-gradient(135deg, #c84400 0%, #e8420a 50%, #ff8c00 100%)',
    shadow: '0 2px 8px rgba(200, 68, 0, 0.25)',
    linkHoverColor: '#ffcc44',
  },
});

// ─── Forest ───────────────────────────────────────────────────────────────────
// Earthy greens on mint — fresh, calm, sustainability-themed.
export const dtForestTheme = createDtTheme({
  variant: 'light',
  background: { default: '#f0f7f0', paper: '#ffffff' },
  text: { primary: '#0a2a0a', secondary: '#3a6a3a' },
  primary: { main: '#2d6e2d', light: '#5a9e5a', dark: '#1a3d1a', contrast: '#ffffff' },
  secondary: { main: '#5a9e5a', light: '#9ecc9e', dark: '#2d6e2d', contrast: '#0a2a0a' },
  border: '#b8d8b8',
  accent: '#9ecc9e',
  action: {
    active: '#2d6e2d',
    hover: '#e0f0e0',
    selected: '#c8e8c8',
    disabled: '#7aaa7a',
    disabledBackground: '#d0e8d0',
  },
  nav: {
    background: '#1a3d1a',
    text: '#e0f0e0',
    selectedText: '#9ecc9e',
    itemHover: '#2d6e2d',
    submenuBg: '#2d6e2d',
  },
  header: {
    background: '#1a3d1a',
    gradient: 'linear-gradient(135deg, #1a3d1a 0%, #2d6e2d 50%, #5a9e5a 100%)',
    shadow: '0 2px 8px rgba(26, 61, 26, 0.2)',
    linkHoverColor: '#9ecc9e',
  },
});
